import { markRaw } from 'vue';
import { isAdmin } from '../services/adminAuth.js';
import { db, firebaseConfig } from '../services/firebase.js';

export const JobsMap = {
  template: "#jobs-map",
  data() {
    return {
      // NOTE: map, markers and gridCells are deliberately NOT in data().
      // Vue wraps anything in data() in a reactive proxy, and Google Maps
      // registers the RAW object internally. Calling marker.setMap(null) on the
      // proxy leaves the map's own registry pointing at the raw object, so the
      // marker stays on screen and cannot be removed. They are plain instance
      // properties, set in created().
      jobs: {},
      users: {},
      showAllJobs: false,
      currentUserId: null,
      loading: true,
      isLoading: false,
      isAdmin: false,
      mapInitialized: false,
      renderPass: 0,
      mappedCount: 0,
      unmappableCount: 0,
      currentMode: "heatmap",
      availableModes: [
        { value: "markers", label: "Markers" },
        { value: "heatmap", label: "Coverage" }
      ],
      serviceTypeColors: {
        "House wash": "#FF0000",
        "Roof wash": "#00FF00",
        "Driveway/Sidewalk": "#0000FF",
        Commercial: "#FFA500",
        Restoration: "#800080"
      }
    };
  },
  created() {
    // Non-reactive on purpose - see the note in data().
    this.map = null;
    this.markers = [];
    this.gridCells = [];
  },
  computed: {
    filteredJobs() {
      // If admin and showing all jobs, return all jobs
      if (this.isAdmin && this.showAllJobs) {
        return this.jobs;
      }

      // If not admin or not showing all jobs, filter by currentUserId
      return Object.fromEntries(
        Object.entries(this.jobs).filter(
          ([_, job]) => job.userId === this.currentUserId
        )
      );
    },
    // An empty map used to say nothing at all. Now it says why.
    mapStatus() {
      const total = Object.keys(this.filteredJobs).length;
      if (!total) {
        return this.isAdmin && !this.showAllJobs
          ? "No jobs on your own account - switch on All operators"
          : "No jobs to map yet";
      }
      if (this.mappedCount === 0) {
        return `${total} job${total === 1 ? "" : "s"}, none with a usable location yet`;
      }
      const missing = this.unmappableCount
        ? ` - ${this.unmappableCount} address${this.unmappableCount === 1 ? "" : "es"} could not be located`
        : "";
      return `${this.mappedCount} of ${total} job${total === 1 ? "" : "s"} mapped${missing}`;
    },
    canShowAllJobs() {
      return this.isAdmin;
    },
    uniqueVendors() {
      return [
        ...new Set(Object.values(this.filteredJobs).map((job) => job.vendor))
      ];
    },
    uniqueServiceTypes() {
      return [
        ...new Set(
          Object.values(this.filteredJobs).map((job) => job.serviceType)
        )
      ];
    }
  },
  methods: {
    async checkAdminStatus() {
      try {
        const user = firebase.auth().currentUser;
        if (!user) return;

        this.isAdmin = await isAdmin();
      } catch (err) {
        console.error("Error checking admin status:", err);
        this.isAdmin = false;
      }
    },

    async fetchData() {
      try {
        // If admin, fetch all jobs
        if (this.isAdmin) {
          const jobsSnapshot = await db.ref("jobs").once("value");
          this.jobs = jobsSnapshot.val() || {};
        } else {
          // If regular user, only fetch their jobs
          const jobsSnapshot = await db
            .ref("jobs")
            .orderByChild("userId")
            .equalTo(this.currentUserId)
            .once("value");
          this.jobs = jobsSnapshot.val() || {};
        }

        await this.$nextTick();
        if (!this.mapInitialized) {
          await this.initMap();
        } else {
          await this.updateVisualization();
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.loading = false;
      }
    },

    toggleShowAllJobs(event) {
      // Only sets the flag. A watcher on showAllJobs already re-renders, and
      // calling updateVisualization() here too ran two passes concurrently:
      // one cleared gridCells while the other was still pushing rectangles into
      // it, leaving those rectangles drawn but untracked - so switching to
      // Markers could no longer remove them.
      this.showAllJobs = event.target.checked;
    },

    getMarkerColor(serviceType) {
      return this.serviceTypeColors[serviceType] || "#000000";
    },

    async initMap() {
      if (!this.$refs.mapContainer) return;

      const defaultCenter = { lat: 28.5384, lng: -81.3789 };
      this.map = markRaw(new google.maps.Map(this.$refs.mapContainer, {
        zoom: 8,
        center: defaultCenter,
        styles: [
          {
            featureType: "all",
            elementType: "geometry",
            stylers: [
              {
                color: "#f5f5f5"
              }
            ]
          },
          {
            featureType: "all",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#616161"
              }
            ]
          },
          {
            featureType: "all",
            elementType: "labels.text.stroke",
            stylers: [
              {
                color: "#f5f5f5"
              }
            ]
          },
          {
            featureType: "administrative.land_parcel",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#bdbdbd"
              }
            ]
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [
              {
                color: "#eeeeee"
              }
            ]
          },
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [
              {
                visibility: "off"
              }
            ]
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [
              {
                color: "#ffffff"
              }
            ]
          },
          {
            featureType: "road.arterial",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#757575"
              }
            ]
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [
              {
                color: "#dadada"
              }
            ]
          },
          {
            featureType: "road.highway",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#616161"
              }
            ]
          },
          {
            featureType: "road.local",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#9e9e9e"
              }
            ]
          },
          {
            featureType: "transit.line",
            elementType: "geometry",
            stylers: [
              {
                color: "#e5e5e5"
              }
            ]
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [
              {
                color: "#e9e9e9"
              }
            ]
          },
          {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [
              {
                color: "#9e9e9e"
              }
            ]
          }
        ]
      }));

      this.mapInitialized = true;
      await this.updateVisualization();
    },

    async getJobCoordinates() {
      // This used to geocode every job on every render, serially - one billed
      // Geocoding request per job, per map view, per mode switch. Jobs now carry
      // lat/lng captured from Places at creation, so the common path costs
      // nothing. Anything still missing coordinates is geocoded ONCE and the
      // result written back, so it never costs again.
      const coordinates = [];
      const needsGeocoding = [];

      // entries(), not values(): filteredJobs is keyed by job id and the job
      // objects do not carry one, so the write-back below would never fire.
      for (const [jobId, job] of Object.entries(this.filteredJobs)) {
        const withId = { ...job, id: job.id || jobId };
        if (typeof job.lat === "number" && typeof job.lng === "number") {
          coordinates.push({
            position: new google.maps.LatLng(job.lat, job.lng),
            job: withId,
          });
        } else if (job.serviceLocation) {
          needsGeocoding.push(withId);
        }
      }

      if (needsGeocoding.length) {
        console.log(`Geocoding ${needsGeocoding.length} job(s) missing coordinates (one time).`);
        const geocoder = new google.maps.Geocoder();
        for (const job of needsGeocoding) {
          try {
            const position = await new Promise((resolve, reject) => {
              geocoder.geocode({ address: job.serviceLocation }, (results, status) => {
                if (status === "OK") resolve(results[0].geometry.location);
                else reject(new Error(status));
              });
            });
            coordinates.push({ position, job });
            // Persist so this address is never geocoded again. Best effort: a
            // failed write costs a future lookup, not this render.
            if (job.id) {
              db.ref(`jobs/${job.id}`)
                .update({ lat: position.lat(), lng: position.lng() })
                .catch((e) => console.warn("Could not store coordinates:", e.message));
            }
          } catch (error) {
            console.error(`Geocoding failed for ${job.serviceLocation}:`, error.message);
          }
        }
      }

      this.mappedCount = coordinates.length;
      this.unmappableCount = Object.keys(this.filteredJobs).length - coordinates.length;
      return coordinates;
    },

    clearVisualizations() {
      if (this.markers.length) {
        this.markers.forEach((marker) => marker.setMap(null));
        this.markers = [];
      }

      if (this.gridCells.length) {
        this.gridCells.forEach((cell) => cell.setMap(null));
        this.gridCells = [];
      }
    },

    async updateVisualization() {
      // Renders can overlap: a mode click, the showAllJobs watcher and the jobs
      // listener can all trigger one, and getJobCoordinates() may await
      // geocoding in between. Stamp each pass and let a superseded pass bail
      // before it draws, so only the newest render owns what is on the map.
      const pass = (this.renderPass || 0) + 1;
      this.renderPass = pass;

      this.clearVisualizations();
      const coordinates = await this.getJobCoordinates();
      if (pass !== this.renderPass) return;

      if (coordinates.length === 0) return;

      if (this.currentMode === "markers") {
        await this.showMarkers(coordinates);
      } else if (this.currentMode === "heatmap") {
        await this.showCoverageGrid(coordinates);
      }
    },

    async showMarkers(coordinates) {
      // Tracked as each marker is created, not assigned in one go at the end.
      // A marker is on the map the moment it is constructed, so assigning the
      // whole array afterwards leaves a window where markers exist but nothing
      // knows about them - anything that clears during that window orphans them
      // on the map permanently, with no reference left to remove them.
      for (const { position, job } of coordinates) {
        const marker = new google.maps.Marker({
          position,
          map: this.map,
          title: job.customerName,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: this.getMarkerColor(job.serviceType),
            fillOpacity: 0.9,
            strokeWeight: 2,
            strokeColor: "#FFFFFF",
            scale: 8
          }
        });

        const streetViewUrl = `https://www.google.com/maps?layer=c&cbll=${position.lat()},${position.lng()}`;
        const infoWindow = new google.maps.InfoWindow({
          content: `
                          <div style="max-width: 200px;">
                              <h6 class="mb-2">${job.customerName}</h6>
                              <p class="mb-1"><strong>Service:</strong> ${job.serviceType}</p>
                              <p class="mb-1"><strong>Location:</strong> ${job.serviceLocation}</p>
                              <p class="mb-1"><strong>Vendor:</strong> ${job.vendor}</p>
                              <p class="mb-1"><strong>Quote:</strong> ${job.quotedPrice}</p>
                              <p class="mb-1"><strong>Date:</strong> ${job.dateSubmitted}</p>
                              <p class="mb-0"><a href="${streetViewUrl}" target="_blank">View Street View</a></p>
                          </div>
                      `
        });

        marker.addListener("click", () => {
          infoWindow.open(this.map, marker);
        });

        this.markers.push(markRaw(marker));
      }
    },

    // Replaces google.maps.visualization.HeatmapLayer, which Google deprecated.
    //
    // Buckets jobs into a fixed geographic grid and shades each cell by how many
    // fall inside it. For service-area coverage this arguably reads better than a
    // blurred heat gradient: a cell is a real area you can quote, and the count
    // is exact rather than implied by colour intensity. Built from Rectangle and
    // InfoWindow, so no deprecated API and no extra dependency.
    async showCoverageGrid(coordinates) {
      if (!coordinates.length) return;

      // ~0.02 degrees latitude is roughly 2.2 km. Longitude cells narrow as you
      // move away from the equator; at Orlando's latitude that is close enough
      // to square for reading coverage.
      const CELL = 0.02;
      const cells = new Map();

      for (const { position, job } of coordinates) {
        const lat = typeof position.lat === "function" ? position.lat() : position.lat;
        const lng = typeof position.lng === "function" ? position.lng() : position.lng;
        const row = Math.floor(lat / CELL);
        const col = Math.floor(lng / CELL);
        const key = `${row}:${col}`;
        if (!cells.has(key)) cells.set(key, { row, col, jobs: [] });
        cells.get(key).jobs.push(job);
      }

      const busiest = Math.max(...[...cells.values()].map((c) => c.jobs.length));

      for (const cell of cells.values()) {
        const share = cell.jobs.length / busiest;
        this.gridCells.push(
          markRaw(new google.maps.Rectangle({
            map: this.map,
            bounds: {
              south: cell.row * CELL,
              north: (cell.row + 1) * CELL,
              west: cell.col * CELL,
              east: (cell.col + 1) * CELL,
            },
            // Light blue through to deep blue, so a single job is still visible
            // rather than fading into the map.
            fillColor: share > 0.66 ? "#1d4ed8" : share > 0.33 ? "#3b82f6" : "#93c5fd",
            fillOpacity: 0.25 + share * 0.45,
            strokeColor: "#1e40af",
            strokeOpacity: 0.35,
            strokeWeight: 1,
            clickable: true,
          }))
        );

        const rectangle = this.gridCells[this.gridCells.length - 1]; // pushed above, already tracked
        rectangle.addListener("click", (event) => {
          const types = cell.jobs.reduce((acc, j) => {
            const t = j.serviceType || "Service";
            acc[t] = (acc[t] || 0) + 1;
            return acc;
          }, {});
          const rows = Object.entries(types)
            .sort((a, b) => b[1] - a[1])
            .map(([t, n]) => `<div>${n} &times; ${t}</div>`)
            .join("");
          new google.maps.InfoWindow({
            position: event.latLng,
            content:
              `<div style="font-family:system-ui;font-size:13px">` +
              `<strong>${cell.jobs.length} job${cell.jobs.length === 1 ? "" : "s"}</strong>` +
              `<div style="margin-top:4px;color:#475569">${rows}</div></div>`,
          }).open(this.map);
        });
      }
    },

    async changeMode(mode) {
      if (this.currentMode === mode || this.isLoading) return;

      try {
        this.isLoading = true;
        this.currentMode = mode;
        await this.updateVisualization();
      } catch (error) {
        console.error("Error changing visualization mode:", error);
      } finally {
        this.isLoading = false;
      }
    }
  },
  watch: {
    showAllJobs: {
      async handler() {
        try {
          this.isLoading = true;
          await this.updateVisualization();
        } catch (error) {
          console.error("Error updating visualization:", error);
        } finally {
          this.isLoading = false;
        }
      }
    }
  },
  async mounted() {
    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    }

    firebase.auth().onAuthStateChanged(async (user) => {
      if (user) {
        this.currentUserId = user.uid;
        await this.checkAdminStatus(); // Check admin status first
        await this.fetchData(); // Then fetch appropriate data
      } else {
        this.currentUserId = null;
        this.isAdmin = false;
        this.loading = false;
      }
    });
  },
  beforeUnmount() {
    this.clearVisualizations();
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
  }
};
